import * as UiUtil from "@/util/UiUtil";

export const getFieldLabel = () => {
  const domain = window.location.hostname;
  switch (domain) {
  case "ep.maot.co.kr":
    return "작업장명";
  case "nhsafe.maot.co.kr":
    return "현장명";
  default:
    return "사업장명";
  }
};

/**
 * fetch 응답을 파일로 저장한다.
 * - 응답이 오류면 그 내용을 파일로 저장하지 않고 사유를 알린다
 * - 저장에 쓴 임시 링크와 URL 은 바로 정리한다
 *
 * @param {Response} response fetch 응답
 * @param {String} fileName 저장할 파일 이름
 */
export const saveResponseAsFile = async (response, fileName) => {
  if (!response.ok) {
    let message = "";
    try {
      const text = await response.text();
      try {
        const data = JSON.parse(text);
        message = typeof data === "string" ? data : (data && typeof data.message === "string" ? data.message : "");
      } catch (e) {
        message = text.length < 200 ? text : "";
      }
    } catch (e) { /* 본문을 읽지 못하면 기본 문구를 쓴다 */ }
    UiUtil.alertDialog(message || "파일을 내려받지 못했습니다.");
    return;
  }

  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", fileName);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  // 다운로드가 시작된 뒤에 URL 을 해제한다
  setTimeout(() => window.URL.revokeObjectURL(url), 1000);
};


/**
 * 레벤슈타인 거리(Levenshtein Distance)를 계산하는 함수입니다.
 * 이 함수는 두 문자열 간의 '편집 거리'를 측정합니다.
 * 편집 거리란 한 문자열을 다른 문자열로 변환하기 위해 필요한 최소한의 편집 연산(삽입, 삭제, 대체)의 수를 의미합니다.
 *
 * @param {string} a - 첫 번째 문자열
 * @param {string} b - 두 번째 문자열
 * @returns {number} 두 문자열 간의 레벤슈타인 거리
 */
export function levenshteinDistance(a, b) {
  const matrix = [];

  // 첫 번째 행을 초기화합니다.
  for (let i = 0; i <= b.length; i++) {
    matrix[i] = [i];
  }

  // 첫 번째 열을 초기화합니다.
  for (let j = 0; j <= a.length; j++) {
    matrix[0][j] = j;
  }

  // 행렬을 채웁니다.
  for (let i = 1; i <= b.length; i++) {
    for (let j = 1; j <= a.length; j++) {
      // 문자가 같으면 대각선 위의 값(편집 거리가 증가하지 않음)을 가져옵니다.
      if (b.charAt(i - 1) === a.charAt(j - 1)) {
        matrix[i][j] = matrix[i - 1][j - 1];
      } else {
        // 문자가 다르면 삽입, 삭제, 대체 중에서 편집 거리가 가장 작은 연산을 선택합니다.
        matrix[i][j] = Math.min(matrix[i - 1][j - 1] + 1, Math.min(matrix[i][j - 1] + 1, matrix[i - 1][j] + 1));
      }
    }
  }

  // 마지막으로, 두 문자열 간의 레벤슈타인 거리를 반환합니다.
  return matrix[b.length][a.length];
}

// post.post_contents 값이 존재하는 경우,
// 여기에는 [{post_id, data_key, data_value}, ...] 형태로 데이터가 들어있다.
// data_key 예시 : body.inspectList_1[6].isChecked
// 이 값들을 하나의 오브젝트로 합쳐서 반환한다.
export const mergePostContents = (post) => {
  if (!post || !post.post_contents || !Array.isArray(post.post_contents)) return {};

  const postContents = post.post_contents;
  if (!postContents || postContents.length === 0) return {};

  const merged = {};
  postContents.forEach((content) => {
    const key = content.data_key;
    const value = content.data_value;
    if (key && value !== undefined) {
      const keys = key.split(".");
      let target = merged;
      keys.forEach((k, i) => {
        const arrayMatch = k.match(/(.+)\[(\d+)]$/); // 배열 키 매칭
        if (arrayMatch) {
          const arrayKey = arrayMatch[1];
          const index = parseInt(arrayMatch[2], 10);

          // 배열 초기화
          if (!target[arrayKey]) target[arrayKey] = [];
          if (!Array.isArray(target[arrayKey])) throw new Error(`${arrayKey} is not an array`);

          // 배열 인덱스에 null 체크
          if (!target[arrayKey][index]) target[arrayKey][index] = i === keys.length - 1 ? value : {}; // 마지막 키일 경우 값 할당
          target = target[arrayKey][index]; // 다음 단계로 타겟 이동
        } else {
          // 객체 초기화
          if (i === keys.length - 1) {
            target[k] = value; // 마지막 키에 값 할당
          } else {
            if (!target[k] || target[k] === null) target[k] = {}; // 객체 초기화
            target = target[k];
          }
        }
      });
    }
  });

  return merged;
};

/**
 * 주어진 배열에서 쿼리 문자열과 가장 유사한 요소를 찾는 함수입니다.
 * 이 함수는 레벤슈타인 거리(Levenshtein Distance)를 사용하여 쿼리 문자열과 각 배열 요소 간의 '편집 거리'를 계산하고,
 * 가장 작은 편집 거리를 가진 요소를 반환합니다.
 *
 * @param {Array} arr - 검색 대상이 되는 문자열 배열
 * @param {string} query - 가장 유사한 요소를 찾고자 하는 쿼리 문자열
 * @returns {string} 쿼리 문자열과 가장 유사한 배열 요소
 */
export function findMostSimilar(arr, query) {
  return arr.reduce((a, b) => {
    return levenshteinDistance(query, a) < levenshteinDistance(query, b) ? a : b;
  });
}


let _loadDefragmentation_list = [];
const _loadDefragmentation = async ($store, apiType, options, useLoading) => {
  const response = await $store.dispatch(apiType, options);
  const list = [];
  list.push(...response.data);
  _loadDefragmentation_list.push(...response.data);
  if (useLoading) {
    UiUtil.loading(true, _loadDefragmentation_list.length, response.total);
  }
  if (response.current_page !== response.last_page) {
    options.page = response.current_page + 1;
    const _list = await _loadDefragmentation($store, apiType, options, useLoading);
    list.push(..._list);
    _loadDefragmentation_list.push(..._list);
  }
  return list
};

/**
 * 모든 페이지의 데이터를 불러온다.
 * @param $store
 * @param apiType
 * @param options
 * @returns {Promise<*[]>}
 */
export async function loadDefragmentation($store, apiType, options, useLoading) {
  _loadDefragmentation_list = [];
  if (useLoading) {
    UiUtil.loading(true);
  }
  const list = await _loadDefragmentation($store, apiType, options, useLoading)
  if (useLoading) {
    UiUtil.loading(false);
  }
  return list;
}

export function comma(str) {
  return `${str}`.replace(/\D/g, '').replace(/\B(?=(\d{3})+(?!\d))/g, ',')
}
